import { FormulaQuery, FormulaTemplateQuery } from 'dashboard-services/api/formula';
import { CommonService } from 'dashboard-services/services';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export default (() => {
  // Formula dependency trees contain resolved template nodes, so they are refreshed together with the templates.
  const useInvalidateTemplates = ({ onSuccess }) => {
    const queryClient = useQueryClient()
    return (...args) => {
      queryClient.invalidateQueries({ queryKey: FormulaTemplateQuery.KEYS.ALL })
      queryClient.invalidateQueries({ queryKey: FormulaQuery.KEYS.GET_DEPENDENCY_TREE() })
      return onSuccess?.(...args)
    }
  }

  // `name` is an exact match; `query` takes extra Datalake filters in `field=value&field=value` form (e.g. `name.keyword=*rev*`).
  const useSearchFormulaTemplates = ({ getQueryBuilder, groupName, name, latest, tags, query, size = CommonService.getRequestSize(), enabled = true, onSelect, shouldRefetch = true, shouldBeLimited, ...rest }) =>
    useInfiniteQuery({
      ...FormulaTemplateQuery.QUERIES.search(
        getQueryBuilder,
        {
          groupName,
          name,
          latest,
          tags,
          query,
          size
        }
      ),
      refetchInterval: () => !shouldRefetch || shouldBeLimited?.() ? undefined : 10_000,
      enabled,
      select: onSelect,
      ...rest
    })

  const useSearchFormulaTemplatesMutation = ({ getQueryBuilder, groupName, name, latest, tags, query, size = CommonService.getRequestSize(), ...rest }) =>
    useMutation({
      ...FormulaTemplateQuery.QUERIES.searchMutation(
        getQueryBuilder,
        {
          groupName,
          name,
          latest,
          tags,
          query,
          size
        }
      ),
      ...rest
    })

  const useGetFormulaTemplateTags = ({ getQueryBuilder, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaTemplateQuery.QUERIES.getTags(getQueryBuilder),
      enabled,
      ...rest
    })

  const useGetFormulaTemplateById = ({ getQueryBuilder, fileId, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaTemplateQuery.QUERIES.getById(
        getQueryBuilder,
        {
          fileId
        }
      ),
      enabled: enabled && !!fileId,
      ...rest
    })

  const useGetFormulaTemplateByIdMutation = ({ getQueryBuilder, fileId, ...rest }) =>
    useMutation({
      ...FormulaTemplateQuery.QUERIES.getByIdMutation(
        getQueryBuilder,
        {
          fileId
        }
      ),
      ...rest
    })

  const useGetFormulaTemplate = ({ getQueryBuilder, groupName, name, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaTemplateQuery.QUERIES.getByName(
        getQueryBuilder,
        {
          groupName,
          name
        }
      ),
      enabled: enabled && !!groupName && !!name,
      ...rest
    })

  const useGetFormulaTemplateVersion = ({ getQueryBuilder, groupName, name, version, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaTemplateQuery.QUERIES.getVersion(
        getQueryBuilder,
        {
          groupName,
          name,
          version
        }
      ),
      enabled: enabled && !!groupName && !!name && version !== undefined && version !== null,
      ...rest
    })

  const useCreateFormulaTemplate = ({ getQueryBuilder, name, groupName, tags, fields, content, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaTemplateQuery.QUERIES.create(
        getQueryBuilder,
        {
          name,
          groupName,
          tags,
          fields,
          content
        }
      ),
      onSuccess: useInvalidateTemplates({ onSuccess }),
      ...rest
    })

  // Uploads a new revision of the latest template (`fileId`); group/name cannot change.
  // The revision stores exactly the metadata sent: pass `tags` (or keep `tags` in `fields`) to carry them over; `[]` clears them.
  const useUpdateFormulaTemplate = ({ getQueryBuilder, fileId, name, groupName, tags, fields, content, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaTemplateQuery.QUERIES.update(
        getQueryBuilder,
        {
          fileId,
          name,
          groupName,
          tags,
          fields,
          content
        }
      ),
      onSuccess: useInvalidateTemplates({ onSuccess }),
      ...rest
    })

  // Without `force`, deleting the latest revision returns 400 listing the formulas that use the template;
  // retry with `force: true` after the user confirms.
  const useDeleteFormulaTemplateById = ({ getQueryBuilder, fileId, force, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaTemplateQuery.QUERIES.deleteById(
        getQueryBuilder,
        {
          fileId,
          force
        }
      ),
      onSuccess: useInvalidateTemplates({ onSuccess }),
      ...rest
    })

  // Deletes all revisions. Same `force` behaviour as useDeleteFormulaTemplateById.
  const useDeleteFormulaTemplate = ({ getQueryBuilder, groupName, name, force, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaTemplateQuery.QUERIES.delete(
        getQueryBuilder,
        {
          groupName,
          name,
          force
        }
      ),
      onSuccess: useInvalidateTemplates({ onSuccess }),
      ...rest
    })

  return {
    useSearchFormulaTemplates,
    useSearchFormulaTemplatesMutation,
    useGetFormulaTemplateTags,
    useGetFormulaTemplateById,
    useGetFormulaTemplateByIdMutation,
    useGetFormulaTemplate,
    useGetFormulaTemplateVersion,
    useCreateFormulaTemplate,
    useUpdateFormulaTemplate,
    useDeleteFormulaTemplateById,
    useDeleteFormulaTemplate
  }
})()
